import { supabase } from "@/integrations/supabase/client";
import { ACTIVE_KANBAN_STAGE_IDS } from "@/components/jobs/kanban/types/index";
import type { TFunction } from "i18next";

type KanbanBoardKey = "matches";

export interface KanbanColumnDef {
	id: string;
	label?: string;
	labels?: { en?: string; fr?: string; nl?: string };
	color?: string;
	hidden?: boolean;
	isSystem: boolean;
}

export type KanbanColumnsConfig = Partial<Record<KanbanBoardKey, KanbanColumnDef[]>>;

const SYSTEM_MATCH_COLUMN_IDS: string[] = [...ACTIVE_KANBAN_STAGE_IDS, "refused"];

export const SYSTEM_MATCH_COLUMNS: KanbanColumnDef[] = SYSTEM_MATCH_COLUMN_IDS.map((id) => ({ id, isSystem: true }));

export interface KanbanColumnColor {
	color: string;
	bgColor: string;
	borderColor: string;
	headerBg: string;
	headerText: string;
	dotColor: string;
}

export const COLUMN_COLOR_PALETTE: Record<string, KanbanColumnColor> = {
	gray: {
		color: "text-gray-600 dark:text-gray-300",
		bgColor: "bg-gray-50 dark:bg-gray-800/50",
		borderColor: "border-gray-200 dark:border-gray-700",
		headerBg: "bg-gray-100 dark:bg-gray-800",
		headerText: "text-gray-800 dark:text-gray-200",
		dotColor: "bg-gray-500"
	},
	blue: {
		color: "text-blue-600 dark:text-blue-300",
		bgColor: "bg-blue-50 dark:bg-blue-900/20",
		borderColor: "border-blue-200 dark:border-blue-800",
		headerBg: "bg-blue-100 dark:bg-blue-900/40",
		headerText: "text-blue-800 dark:text-blue-200",
		dotColor: "bg-blue-500"
	},
	green: {
		color: "text-green-600 dark:text-green-300",
		bgColor: "bg-green-50 dark:bg-green-900/20",
		borderColor: "border-green-200 dark:border-green-800",
		headerBg: "bg-green-100 dark:bg-green-900/40",
		headerText: "text-green-800 dark:text-green-200",
		dotColor: "bg-green-500"
	},
	purple: {
		color: "text-purple-600 dark:text-purple-300",
		bgColor: "bg-purple-50 dark:bg-purple-900/20",
		borderColor: "border-purple-200 dark:border-purple-800",
		headerBg: "bg-purple-100 dark:bg-purple-900/40",
		headerText: "text-purple-800 dark:text-purple-200",
		dotColor: "bg-purple-500"
	},
	amber: {
		color: "text-amber-600 dark:text-amber-300",
		bgColor: "bg-amber-50 dark:bg-amber-900/20",
		borderColor: "border-amber-200 dark:border-amber-800",
		headerBg: "bg-amber-100 dark:bg-amber-900/40",
		headerText: "text-amber-800 dark:text-amber-200",
		dotColor: "bg-amber-500"
	},
	red: {
		color: "text-red-600 dark:text-red-300",
		bgColor: "bg-red-50 dark:bg-red-900/20",
		borderColor: "border-red-200 dark:border-red-800",
		headerBg: "bg-red-100 dark:bg-red-900/40",
		headerText: "text-red-800 dark:text-red-200",
		dotColor: "bg-red-500"
	},
	teal: {
		color: "text-teal-600 dark:text-teal-300",
		bgColor: "bg-teal-50 dark:bg-teal-900/20",
		borderColor: "border-teal-200 dark:border-teal-800",
		headerBg: "bg-teal-100 dark:bg-teal-900/40",
		headerText: "text-teal-800 dark:text-teal-200",
		dotColor: "bg-teal-500"
	},
	pink: {
		color: "text-pink-600 dark:text-pink-300",
		bgColor: "bg-pink-50 dark:bg-pink-900/20",
		borderColor: "border-pink-200 dark:border-pink-800",
		headerBg: "bg-pink-100 dark:bg-pink-900/40",
		headerText: "text-pink-800 dark:text-pink-200",
		dotColor: "bg-pink-500"
	}
};

export const COLUMN_COLOR_KEYS = Object.keys(COLUMN_COLOR_PALETTE);

// Re-inserts missing system columns at their canonical positions, drops
// unknown system ids and duplicates, and forces isSystem correctness.
export function repairMatchColumns(defs?: KanbanColumnDef[] | null): KanbanColumnDef[] {
	if (!defs || !Array.isArray(defs) || defs.length === 0)
		return SYSTEM_MATCH_COLUMNS.map((c) => ({ ...c }));

	const seen = new Set<string>();
	const repaired: KanbanColumnDef[] = [];
	for (const def of defs) {
		if (!def || typeof def.id !== "string" || seen.has(def.id)) continue;
		const isSystem = SYSTEM_MATCH_COLUMN_IDS.includes(def.id);
		if (!isSystem && !def.id.startsWith("custom_")) continue;
		seen.add(def.id);
		repaired.push({ ...def, isSystem });
	}

	// Missing system columns come back at their canonical index (refused last)
	SYSTEM_MATCH_COLUMN_IDS.forEach((id, index) => {
		if (seen.has(id)) return;
		const insertAt = id === "refused" ? repaired.length : Math.min(index, repaired.length);
		repaired.splice(insertAt, 0, { id, isSystem: true });
		seen.add(id);
	});

	return repaired;
}

export function makeCustomColumnId(label: string, existingIds: string[]): string {
	const slug = label
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 40) || "column";
	let id = `custom_${slug}`;
	let suffix = 2;
	while (existingIds.includes(id))
		id = `custom_${slug}_${suffix++}`;
	return id;
}

export function getMatchStatusLabel(
	t: TFunction,
	statusId: string,
	columns?: KanbanColumnDef[],
	language?: string
): string {
	const column = columns?.find((c) => c.id === statusId);
	if (column && !column.isSystem) {
		const lang = (language || "en").split("-")[0] as keyof NonNullable<KanbanColumnDef["labels"]>;
		return column.labels?.[lang] ?? column.label ?? statusId;
	}
	return t(`matching.statusLabels.${statusId}`, statusId);
}

export async function fetchKanbanColumns(organizationId: string): Promise<KanbanColumnsConfig> {
	const { data, error } = await supabase
		.from("kanban_columns")
		.select("columns")
		.eq("organization_id", organizationId)
		.maybeSingle();
	if (error) throw error;
	return (data?.columns ?? {}) as KanbanColumnsConfig;
}

export async function upsertKanbanColumns(organizationId: string, config: KanbanColumnsConfig) {
	const { error } = await supabase
		.from("kanban_columns")
		.upsert([
			{
				organization_id: organizationId,
				columns: config,
				updated_at: new Date().toISOString()
			}
		], { onConflict: "organization_id" });
	if (error) throw error;
}
